import currency from "currency.js";
import { SideResponse } from "../../codegen-api";
import {
  FIELD_COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import {
  formatCurrencyWithSubscript,
} from "../../utils/format";
import { getProfitTextColor } from "../../utils/strings";
import { Chevron } from "../shared/Chevron/style";
import Checkbox from "../shared/Checkbox";
import Dropdown from "../shared/Dropdown";
import { Input } from "../shared/Input";
import { ModalErrorMessage } from "../shared/style";
import { Form, InputWrapper, Label } from "../TradeForm/style";
import {
  TPSLAdvancedFields,
  TPSLTriggerSource,
} from "./TPSLAdvancedControls";
import {
  CheckRow,
  ChevronWrap,
  HelpText,
  PairRow,
} from "./TPSLForPositionModal.helpers";
import { RenderTpslFormProps, Tpsl } from "./TPSLForPositionModal.types";
import { DropdownItem, TextWrapper } from "./style";

export const renderPctAccessory = (
  open: boolean,
  setOpen: (b: boolean) => void,
  type: typeof Tpsl[keyof typeof Tpsl],
  setType: (t: typeof Tpsl[keyof typeof Tpsl]) => void
) => (
  <Dropdown
    onToggle={setOpen}
    dropdownMenuContainerStyles={{
      overflow: "hidden",
      backgroundColor: LAYER_COLORS.three,
      textAlign: "center",
    }}
    title={
      <ChevronWrap>
        <span>{type === Tpsl.Roi ? "%" : "USD"}</span>
        <Chevron size="small" direction={open ? "up" : "down"} />
      </ChevronWrap>
    }
    items={Object.values(Tpsl).map((v) => ({
      label: (
        <DropdownItem selected={v === type}>
          <span>{v === Tpsl.Roi ? "%" : "USD"}</span>
        </DropdownItem>
      ),
      onSelect: () => setType(v),
    }))}
  />
);

export function renderTpslForm({
  position,
  errors,
  handleSubmit,
  onConfirmTPSLForPosition,
  tpPriceRegister,
  lastEditedTp,
  tpType,
  setTpType,
  tpGainRegister,
  tpDropdownOpen,
  setTpDropdownOpen,
  actualTpTriggerPrice,
  pricePrecision,
  takeProfitExpectedPnl,
  slPriceRegister,
  lastEditedSl,
  slType,
  setSlType,
  slLossRegister,
  slDropdownOpen,
  setSlDropdownOpen,
  actualSlTriggerPrice,
  stopLossExpectedPnl,
  advanced,
  closeSizeRegister,
  tpLimitPriceRegister,
  slLimitPriceRegister,
  asset,
  amountPrecision,
  amountStep,
  markPrice,
  indexPrice,
  lastPrice,
  t,
  errorMessage,
}: RenderTpslFormProps) {
  if (!position) {
    return undefined;
  }

  const tpPriceError = errors?.tpTriggerPrice;
  const tpRoiError = errors?.tpRoi;
  const slPriceError = errors?.slTriggerPrice;
  const slRoiError = errors?.slRoi;

  return (
    <>
      <Form
        style={{ padding: 0, marginTop: SPACING.three, marginBottom: SPACING.three }}
        onSubmit={handleSubmit(onConfirmTPSLForPosition)}
      >
        <TPSLTriggerSource
          priceSource={advanced.priceSource}
          setPriceSource={advanced.setPriceSource}
          indexPrice={indexPrice}
          lastPrice={lastPrice}
          pricePrecision={pricePrecision}
          t={t}
        />

        <PairRow>
          <InputWrapper style={{ width: "100%" }}>
            <Label>TP Price</Label>
            <Input
              error={Boolean(tpPriceError?.type)}
              placeholder="0.00"
              type="number"
              inputStyles={{ background: FIELD_COLORS.one }}
              {...tpPriceRegister}
              onChange={(e) => {
                lastEditedTp.current = "price";
                if (tpType !== Tpsl.Price) setTpType(Tpsl.Price);
                return tpPriceRegister.onChange(e);
              }}
            />
          </InputWrapper>
          <InputWrapper style={{ width: "100%" }}>
            <Label>Gain</Label>
            <Input
              error={Boolean(tpRoiError?.type)}
              placeholder="0"
              type="number"
              rightAccessory={renderPctAccessory(
                tpDropdownOpen,
                setTpDropdownOpen,
                tpType,
                setTpType
              )}
              {...tpGainRegister}
              onChange={(e) => {
                lastEditedTp.current = "roi";
                if (tpType !== Tpsl.Roi) setTpType(Tpsl.Roi);
                return tpGainRegister.onChange(e);
              }}
            />
          </InputWrapper>
        </PairRow>

        {actualTpTriggerPrice ? (
          <TextWrapper>
            TP triggers at{" "}
            <p style={{ color: TEXT_COLORS.one }}>
              {currency(actualTpTriggerPrice, {
                precision: pricePrecision,
              }).format()}
            </p>{" "}
            · est. PnL{" "}
            <p style={{ color: getProfitTextColor(takeProfitExpectedPnl || 0) }}>
              {formatCurrencyWithSubscript(takeProfitExpectedPnl || "0")}
            </p>
          </TextWrapper>
        ) : null}

        <div style={{ marginTop: SPACING.three }}>
          <PairRow>
            <InputWrapper style={{ width: "100%" }}>
              <Label>SL Price</Label>
              <Input
                error={Boolean(slPriceError?.type)}
                placeholder="0.00"
                type="number"
                inputStyles={{ background: FIELD_COLORS.one }}
                {...slPriceRegister}
                onChange={(e) => {
                  lastEditedSl.current = "price";
                  if (slType !== Tpsl.Price) setSlType(Tpsl.Price);
                  return slPriceRegister.onChange(e);
                }}
              />
            </InputWrapper>
            <InputWrapper style={{ width: "100%" }}>
              <Label>Loss</Label>
              <Input
                error={Boolean(slRoiError?.type)}
                placeholder="0"
                type="number"
                rightAccessory={renderPctAccessory(
                  slDropdownOpen,
                  setSlDropdownOpen,
                  slType,
                  setSlType
                )}
                {...slLossRegister}
                onChange={(e) => {
                  lastEditedSl.current = "roi";
                  if (slType !== Tpsl.Roi) setSlType(Tpsl.Roi);
                  return slLossRegister.onChange(e);
                }}
              />
            </InputWrapper>
          </PairRow>
        </div>

        {actualSlTriggerPrice ? (
          <TextWrapper>
            SL triggers at{" "}
            <p style={{ color: TEXT_COLORS.one }}>
              {currency(actualSlTriggerPrice, {
                precision: pricePrecision,
              }).format()}
            </p>{" "}
            · est. PnL{" "}
            <p style={{ color: getProfitTextColor(stopLossExpectedPnl || 0) }}>
              {formatCurrencyWithSubscript(stopLossExpectedPnl || "0")}
            </p>
          </TextWrapper>
        ) : null}

        <CheckRow>
          <Checkbox
            checked={advanced.configureAmount}
            onToggle={advanced.toggleConfigureAmount}
            title={t("configure_amount")}
          />
          <Checkbox
            checked={advanced.useLimitPrice}
            onToggle={advanced.toggleUseLimitPrice}
            title={t("limit_price")}
          />
        </CheckRow>

        <TPSLAdvancedFields
          advanced={advanced}
          closeSizeRegister={closeSizeRegister}
          tpLimitPriceRegister={tpLimitPriceRegister}
          slLimitPriceRegister={slLimitPriceRegister}
          errors={errors}
          asset={asset || position.asset}
          amountPrecision={amountPrecision}
          amountStep={amountStep}
          markPrice={markPrice}
          tpEnabled={Number(actualTpTriggerPrice) > 0}
          slEnabled={Number(actualSlTriggerPrice) > 0}
          isLong={position.side === SideResponse.Buy}
          t={t}
        />
      </Form>
      {errorMessage && <ModalErrorMessage>{errorMessage}</ModalErrorMessage>}

      <HelpText>
        By default take-profit and stop-loss orders apply to the entire
        position. Take-profit and stop-loss automatically cancel after closing
        the position. A market order is triggered when the stop loss or take
        profit price is reached.
      </HelpText>
      <HelpText>
        If the order size is configured above, the TP/SL order will be for
        that size no matter how the position changes in the future.
      </HelpText>
    </>
  );
}
